import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { auth } from "hooks/useFirebase";
import { getImpersonatedUserId } from "hooks/useImpersonation/useImpersonation";
import { logError, LogCategory } from "utils/sentryLogger";

const BACKOFF_INITIAL_MS = 1000;
const BACKOFF_MAX_MS = 30000;
const MAX_WINDOW = 6;
// Waiting for Firebase to restore a session is not a failed connection, so it
// gets a flat retry rather than the backoff — otherwise a slow restore leaves
// the first real attempt paying up to BACKOFF_MAX_MS of accumulated penalty.
const AUTH_WAIT_MS = 1000;
// The server pings well inside this window, so silence for this long means the
// connection is dead. Without it a half-open socket (laptop sleep, VPN flap, a
// proxy dropping an idle connection without a FIN) leaves `reader.read()`
// pending forever: no error, no reconnect, and no pill until a manual reload.
const IDLE_TIMEOUT_MS = 45000;
const IDLE_ERROR = "LiveSessions: stream idle";

const initialState = {
  status: "connecting",
  sessions: {},
  alertProgress: {},
  // session_id -> { status: "processing"|"completed"|"failed", updated_at }.
  // Best-effort push hints for the history list (see session_status handling).
  statusEvents: {},
  // session_ids that received session_ended — guards against stale
  // session_started/update events resurrecting a pill after it was removed.
  tombstones: {},
  // Bumped on every snapshot, i.e. once per (re)connect. The history list has
  // no other way to learn what it missed while the stream was down — the
  // snapshot itself only carries live sessions.
  snapshotCount: 0,
  // session_id -> transcript lines streamed so far (kept across reconnects
  // while the session is live), deduped on event_id. The Transcript tab merges
  // these over its fetch of earlier lines.
  transcripts: {},
};

function mergeLoopIntoWindow(window = [], incoming) {
  const idx = window.findIndex((l) => l.question_id === incoming.question_id);
  let next;
  if (idx === -1) {
    next = [incoming, ...window];
  } else {
    next = window.slice();
    next[idx] = { ...window[idx], ...incoming };
  }
  return next.slice(0, MAX_WINDOW);
}

// Server rosters (snapshot / session_started) may carry a tri-state
// `connected` per participant: null means "no status seen yet" and is NOT an
// override; true/false are folded into participantOverrides so a later update
// event's roster replacement can't erase join/leave state.
function seedParticipantOverrides(participants) {
  const overrides = {};
  for (const p of participants || []) {
    if (p.connected == null) continue;
    overrides[p.student_id] = {
      student_id: p.student_id,
      student_name: p.student_name,
      connected: p.connected,
    };
  }
  return overrides;
}

function applyAlerts(prevProgress, alerts) {
  if (!alerts || alerts.length === 0) return prevProgress;
  const next = { ...prevProgress };
  for (const a of alerts) {
    if (a.state === "unchanged" && a.streak === (prevProgress?.[a.alert_type]?.streak ?? 0)) {
      continue;
    }
    next[a.alert_type] = { state: a.state, streak: a.streak };
  }
  return next;
}

function reducer(state, action) {
  switch (action.type) {
    case "connecting":
      return { ...state, status: "connecting" };
    case "snapshot": {
      const sessions = {};
      const alertProgress = {};
      for (const s of action.snapshot.sessions || []) {
        // Snapshot rosters now carry connected state (server folds
        // participant_status into them), so seeding here is what lets a
        // reconnect remember who was offline.
        sessions[s.session_id] = {
          ...s,
          participantOverrides: seedParticipantOverrides(s.participants),
        };
        alertProgress[s.session_id] = {
          collaboration: {
            state: s.collaboration_alert_raised ? "raised" : "cleared",
            streak: 0,
          },
          proficiency: {
            state: s.proficiency_alert_raised ? "raised" : "cleared",
            streak: 0,
          },
        };
      }
      // Snapshot is authoritative — clear tombstones so future sessions with
      // recycled IDs aren't permanently blocked.
      return {
        ...state,
        status: "live",
        sessions,
        alertProgress,
        transcripts: Object.fromEntries(
          Object.entries(state.transcripts).filter(([id]) => sessions[id]),
        ),
        tombstones: {},
        snapshotCount: state.snapshotCount + 1,
      };
    }
    case "update": {
      const u = action.update;
      if (state.tombstones[u.session_id]) return state;
      // First update for a session id not in the snapshot is normal — see v2.1
      // contract "Common pitfalls" #2. Materialize a session entry from the
      // update payload (group_code + session_started_at travel on every update).
      const prev = state.sessions[u.session_id] || {
        session_id: u.session_id,
        classroom_id: u.classroom_id,
        group_id: u.group_id,
        group_code: u.group_code ?? null,
        session_started_at: u.session_started_at ?? null,
        window: [],
        collaboration_alert_raised: false,
        proficiency_alert_raised: false,
        safety_concern_raised: false,
      };
      const nextWindow = mergeLoopIntoWindow(prev.window, u.loop);
      const nextAlertProgress = applyAlerts(state.alertProgress[u.session_id], u.alerts);
      const collabRaised = nextAlertProgress?.collaboration?.state === "raised";
      const profRaised = nextAlertProgress?.proficiency?.state === "raised";
      return {
        ...state,
        sessions: {
          ...state.sessions,
          [u.session_id]: {
            ...prev,
            classroom_id: u.classroom_id,
            group_id: u.group_id,
            // Late-arriving group_code or session_started_at from any update
            // overrides null but doesn't clobber a value we already had.
            group_code: u.group_code ?? prev.group_code ?? null,
            session_started_at:
              u.session_started_at ?? prev.session_started_at ?? null,
            standard_prefix:
              u.standard_prefix ?? prev.standard_prefix ?? null,
            standard_prefix_name:
              u.standard_prefix_name ?? prev.standard_prefix_name ?? null,
            standard_prefix_description:
              u.standard_prefix_description ??
              prev.standard_prefix_description ??
              null,
            participants: u.participants ?? prev.participants ?? [],
            window: nextWindow,
            collaboration_alert_raised: collabRaised,
            proficiency_alert_raised: profRaised,
          },
        },
        alertProgress: {
          ...state.alertProgress,
          [u.session_id]: nextAlertProgress,
        },
      };
    }
    case "session_started": {
      // Backend publishes this the moment a session goes in-progress so the
      // pill appears immediately, even before Q1 completes. If we already have
      // the session (e.g. snapshot + reconnect raced), preserve the existing
      // window and just refresh the metadata.
      const s = action.event;
      if (state.tombstones[s.session_id]) return state;
      const prev = state.sessions[s.session_id] || {
        window: [],
        collaboration_alert_raised: false,
        proficiency_alert_raised: false,
        safety_concern_raised: false,
      };
      return {
        ...state,
        sessions: {
          ...state.sessions,
          [s.session_id]: {
            ...prev,
            session_id: s.session_id,
            classroom_id: s.classroom_id,
            group_id: s.group_id,
            group_code: s.group_code ?? prev.group_code ?? null,
            session_started_at:
              s.session_started_at ?? prev.session_started_at ?? null,
            // Standards metadata for the session — matches SessionSummary field
            // names so the live pill can render the same code + description.
            standard_prefix: s.standard_prefix ?? prev.standard_prefix ?? null,
            standard_prefix_name:
              s.standard_prefix_name ?? prev.standard_prefix_name ?? null,
            standard_prefix_description:
              s.standard_prefix_description ??
              prev.standard_prefix_description ??
              null,
            participants: s.participants ?? prev.participants ?? [],
            // Seeded values sit under existing overrides so a replayed
            // session_started can't clobber fresher participant_update state.
            participantOverrides: {
              ...seedParticipantOverrides(s.participants),
              ...(prev.participantOverrides || {}),
            },
            collaboration_alert_raised:
              s.collaboration_alert_raised ?? prev.collaboration_alert_raised,
            proficiency_alert_raised:
              s.proficiency_alert_raised ?? prev.proficiency_alert_raised,
            // Sticky: a replayed session_started can't clear a raised concern.
            safety_concern_raised: Boolean(
              s.safety_concern_raised || prev.safety_concern_raised,
            ),
          },
        },
        alertProgress: {
          ...state.alertProgress,
          [s.session_id]: state.alertProgress[s.session_id] || {
            collaboration: { state: "cleared", streak: 0 },
            proficiency: { state: "cleared", streak: 0 },
          },
        },
      };
    }
    case "session_ended": {
      const id = action.event.session_id;
      const { [id]: _omitSession, ...remainingSessions } = state.sessions;
      const { [id]: _omitProgress, ...remainingProgress } = state.alertProgress;
      const { [id]: _omitTranscript, ...remainingTranscripts } = state.transcripts;
      return {
        ...state,
        sessions: remainingSessions,
        alertProgress: remainingProgress,
        transcripts: remainingTranscripts,
        tombstones: { ...state.tombstones, [id]: true },
      };
    }
    case "participant_update": {
      const p = action.event;
      const prev = state.sessions[p.session_id];
      if (!prev) return state;
      // Store in a separate map so update events (which carry a full snapshot
      // of participants) can never clobber mid-session join/leave changes.
      return {
        ...state,
        sessions: {
          ...state.sessions,
          [p.session_id]: {
            ...prev,
            participantOverrides: {
              ...(prev.participantOverrides || {}),
              [p.student_id]: {
                student_id: p.student_id,
                student_name: p.student_name,
                connected: p.connected,
              },
            },
          },
        },
      };
    }
    case "safety_concern": {
      const { session_id: id, event_id: eventId } = action.event;
      const prev = state.sessions[id];
      if (!prev || state.tombstones[id]) return state;
      if (prev.safety_concern_raised && prev.safety_concern_event_id === eventId) return state;
      // The latest event id lets an open pill refetch its flagged quotes.
      return {
        ...state,
        sessions: {
          ...state.sessions,
          [id]: { ...prev, safety_concern_raised: true, safety_concern_event_id: eventId },
        },
      };
    }
    case "transcript_line": {
      const line = action.event;
      const id = line.session_id;
      if (!state.sessions[id] || state.tombstones[id]) return state;
      const lines = state.transcripts[id] || [];
      if (lines.some((l) => l.event_id === line.event_id)) return state;
      return { ...state, transcripts: { ...state.transcripts, [id]: [...lines, line] } };
    }
    case "session_status": {
      // Best-effort hint that a session's analysis status flipped. We DON'T
      // touch live `sessions` here (session_ended handles pill removal) —
      // consumers watch statusEvents to refresh the history list / detail.
      const e = action.event;
      if (!e || !e.session_id) return state;
      const prev = state.statusEvents[e.session_id];
      if (prev && prev.status === e.status && prev.updated_at === e.updated_at) {
        return state;
      }
      return {
        ...state,
        statusEvents: {
          ...state.statusEvents,
          [e.session_id]: { status: e.status, updated_at: e.updated_at },
        },
      };
    }
    case "disabled":
      return { status: "disabled", sessions: {}, alertProgress: {}, statusEvents: {}, tombstones: {}, transcripts: {} };
    case "error":
      return { ...state, status: "error" };
    case "reset":
      return initialState;
    default:
      return state;
  }
}

export function parseSseFrame(frame) {
  let name = "message";
  let data = "";
  for (const rawLine of frame.split("\n")) {
    const line = rawLine.replace(/\r$/, "");
    if (line.startsWith("event:")) {
      name = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      const piece = line.slice(5);
      data += piece.startsWith(" ") ? piece.slice(1) : piece;
    }
  }
  if (!data) return null;
  return { name, data };
}

// SSE spec allows three blank-line separators between frames: \n\n, \r\n\r\n,
// or \r\r. The previous implementation only matched \n\n, which silently dropped
// every frame from servers that emit CRLF.
const FRAME_SEP_RE = /\r\n\r\n|\n\n|\r\r/;

// Any bytes count as alive — including the server's ping comments, which
// `parseSseFrame` drops. Liveness therefore has to be measured here, at the
// read, and not from the frames that make it through.
export async function readWithIdleTimeout(reader, ms) {
  let timer;
  try {
    return await Promise.race([
      reader.read(),
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(IDLE_ERROR)), ms);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

async function readStream(reader, onFrame, signal) {
  let buffer = "";
  while (true) {
    if (signal?.aborted) return;
    let chunk;
    try {
      chunk = await readWithIdleTimeout(reader, IDLE_TIMEOUT_MS);
    } catch (e) {
      // Free the socket before the caller reconnects. Cancelling also settles
      // the read we walked away from, so it can't resolve into a dead buffer.
      try {
        await reader.cancel();
      } catch {}
      throw e;
    }
    const { done, value } = chunk;
    if (done) return;
    buffer += value;
    while (true) {
      const m = buffer.match(FRAME_SEP_RE);
      if (!m) break;
      const split = m.index;
      const sepLen = m[0].length;
      const frame = buffer.slice(0, split);
      buffer = buffer.slice(split + sepLen);
      const event = parseSseFrame(frame);
      if (event) onFrame(event);
    }
  }
}

export function useLiveSessionsStream({ enabled = true } = {}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const abortRef = useRef(null);
  const generationRef = useRef(0);
  const [reconnectNonce, setReconnectNonce] = useState(0);

  // A tab coming back to the foreground, or a network coming back, is the
  // strongest available hint that the stream may have died while we weren't
  // looking — and background tabs throttle the retry timers that would
  // otherwise notice. Reconnecting is cheap and idempotent: the server replies
  // with an authoritative snapshot.
  useEffect(() => {
    const reconnect = () => setReconnectNonce((n) => n + 1);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") reconnect();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("online", reconnect);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("online", reconnect);
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      dispatch({ type: "reset" });
      return;
    }

    const endpoint = process.env.REACT_APP_OKOLABS_API_ENDPOINT;
    if (!endpoint) return;

    const controller = new AbortController();
    abortRef.current = controller;
    const myGeneration = ++generationRef.current;
    // Boxed so onFrame (lifted outside the loop) can mutate via stable refs.
    const ctx = { backoff: BACKOFF_INITIAL_MS, cancelled: false, backoffTimer: null };

    const isStale = () => ctx.cancelled || generationRef.current !== myGeneration;

    const sleep = (ms) =>
      new Promise((resolve) => {
        ctx.backoffTimer = setTimeout(resolve, ms);
      });

    const onFrame = (event) => {
      if (isStale()) return;
      if (event.name === "snapshot") {
        ctx.backoff = BACKOFF_INITIAL_MS;
        try {
          dispatch({ type: "snapshot", snapshot: JSON.parse(event.data) });
        } catch (e) {
          logError("LiveSessions: malformed snapshot frame", e, {
            category: LogCategory.ERROR,
          });
        }
      } else if (event.name === "update") {
        try {
          dispatch({ type: "update", update: JSON.parse(event.data) });
        } catch (e) {
          logError("LiveSessions: malformed update frame", e, {
            category: LogCategory.ERROR,
          });
        }
      } else if (event.name === "session_started") {
        try {
          dispatch({ type: "session_started", event: JSON.parse(event.data) });
        } catch (e) {
          logError("LiveSessions: malformed session_started frame", e, {
            category: LogCategory.ERROR,
          });
        }
      } else if (event.name === "session_ended") {
        try {
          dispatch({ type: "session_ended", event: JSON.parse(event.data) });
        } catch (e) {
          logError("LiveSessions: malformed session_ended frame", e, {
            category: LogCategory.ERROR,
          });
        }
      } else if (event.name === "session_status") {
        try {
          dispatch({ type: "session_status", event: JSON.parse(event.data) });
        } catch (e) {
          logError("LiveSessions: malformed session_status frame", e, {
            category: LogCategory.ERROR,
          });
        }
      } else if (event.name === "participant_update") {
        try {
          dispatch({ type: "participant_update", event: JSON.parse(event.data) });
        } catch (e) {
          logError("LiveSessions: malformed participant_update frame", e, {
            category: LogCategory.ERROR,
          });
        }
      } else if (event.name === "safety_concern") {
        try {
          dispatch({ type: "safety_concern", event: JSON.parse(event.data) });
        } catch (e) {
          logError("LiveSessions: malformed safety_concern frame", e, {
            category: LogCategory.ERROR,
          });
        }
      } else if (event.name === "transcript_line") {
        try {
          dispatch({ type: "transcript_line", event: JSON.parse(event.data) });
        } catch (e) {
          logError("LiveSessions: malformed transcript_line frame", e, {
            category: LogCategory.ERROR,
          });
        }
      } else if (event.name === "disabled") {
        dispatch({ type: "disabled" });
        ctx.cancelled = true;
        try {
          controller.abort();
        } catch {}
      }
      // Forward-compat: unknown event names are dropped silently.
    };

    const connectOnce = async () => {
      let triedTokenRefresh = false;
      while (!isStale()) {
        if (!auth.currentUser) {
          await sleep(AUTH_WAIT_MS);
          continue;
        }
        let token;
        try {
          token = await auth.currentUser.getIdToken(triedTokenRefresh);
        } catch (e) {
          logError("LiveSessions: failed to get Firebase token", e, {
            category: LogCategory.AUTH,
          });
          if (isStale()) return;
          dispatch({ type: "error" });
          await sleep(ctx.backoff);
          ctx.backoff = Math.min(ctx.backoff * 2, BACKOFF_MAX_MS);
          continue;
        }

        const headers = {
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        };
        const impersonated = getImpersonatedUserId();
        if (impersonated) headers["X-Impersonate-User-Id"] = impersonated;

        if (!isStale()) dispatch({ type: "connecting" });

        let response;
        try {
          response = await fetch(`${endpoint}/live/sessions/stream`, {
            method: "GET",
            headers,
            signal: controller.signal,
          });
        } catch (e) {
          if (isStale() || controller.signal.aborted) return;
          dispatch({ type: "error" });
          await sleep(ctx.backoff);
          ctx.backoff = Math.min(ctx.backoff * 2, BACKOFF_MAX_MS);
          continue;
        }

        if (response.status === 401 || response.status === 403) {
          if (!triedTokenRefresh) {
            triedTokenRefresh = true;
            continue;
          }
          if (isStale()) return;
          dispatch({ type: "error" });
          await sleep(ctx.backoff);
          ctx.backoff = Math.min(ctx.backoff * 2, BACKOFF_MAX_MS);
          continue;
        }

        if (!response.ok || !response.body) {
          if (isStale()) return;
          dispatch({ type: "error" });
          await sleep(ctx.backoff);
          ctx.backoff = Math.min(ctx.backoff * 2, BACKOFF_MAX_MS);
          continue;
        }

        triedTokenRefresh = false;
        const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();

        try {
          await readStream(reader, onFrame, controller.signal);
        } catch (e) {
          if (isStale() || controller.signal.aborted) return;
          // Worth recording: a silently dead stream is invisible to the user
          // and was previously invisible to us too.
          if (e?.message === IDLE_ERROR) {
            logError("LiveSessions: stream went idle, reconnecting", e, {
              category: LogCategory.ERROR,
            });
          }
        } finally {
          try {
            reader.releaseLock();
          } catch {}
        }

        if (isStale()) return;
        // Stream ended without disabled — treat as reconnect needed.
        dispatch({ type: "error" });
        await sleep(ctx.backoff);
        ctx.backoff = Math.min(ctx.backoff * 2, BACKOFF_MAX_MS);
      }
    };

    connectOnce();

    return () => {
      ctx.cancelled = true;
      if (ctx.backoffTimer) clearTimeout(ctx.backoffTimer);
      try {
        controller.abort();
      } catch {}
    };
  }, [enabled, reconnectNonce]);

  // A close the teacher made from this page shouldn't wait on the server's
  // session_ended frame (which a dropped stream never delivers) to take the
  // pill down. Same reducer path, so the tombstone guards a late update too.
  const endSession = useCallback(
    (sessionId) => dispatch({ type: "session_ended", event: { session_id: sessionId } }),
    [],
  );

  return { ...state, endSession };
}
